import React from "react";
import { useTranslation } from "react-i18next";
import { Star } from "lucide-react";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { formatCurrencyAmount } from "@/components/pages/services/utils/priceFormatting";

export interface GuesteriaCurationActivityCardProps {
  title: string;
  imageUrl: string | null;
  rating: number | null;
  /**
   * Optional: `PartnerHotelCatalogItem` (the add-dialog's rows) carries a
   * review count, `PartnerHotelCurationItem` (the city list / added-by-hotel
   * rows) does not. When it's `undefined` the rating renders on its own,
   * without the `partners.curation.rating` "{{rating}} ({{count}})" template
   * — see the report note on this gap.
   */
  reviewCount?: number | null;
  price: number | null;
  currency: string | null;
  badge?: React.ReactNode;
  action: React.ReactNode;
  className?: string;
}

export const GuesteriaCurationActivityCard: React.FC<
  GuesteriaCurationActivityCardProps
> = ({
  title,
  imageUrl,
  rating,
  reviewCount,
  price,
  currency,
  badge,
  action,
  className,
}) => {
  const { t } = useTranslation();

  let ratingNode: React.ReactNode;
  if (rating == null) {
    ratingNode = (
      <span className="text-muted-foreground">
        {t("partners.curation.noRating")}
      </span>
    );
  } else if (typeof reviewCount === "number") {
    ratingNode = (
      <span className="flex items-center gap-1">
        <Star className="size-3 fill-current" />
        {t("partners.curation.rating", {
          rating: rating.toFixed(1),
          count: reviewCount,
        })}
      </span>
    );
  } else {
    ratingNode = (
      <span className="flex items-center gap-1">
        <Star className="size-3 fill-current" />
        {rating.toFixed(1)}
      </span>
    );
  }

  return (
    <Card className={cn("flex-row items-center gap-3 p-3", className)}>
      <div className="bg-muted size-16 shrink-0 overflow-hidden rounded-md">
        {imageUrl ? (
          <img src={imageUrl} alt={title} className="size-full object-cover" />
        ) : null}
      </div>

      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex items-center gap-2">
          <p className="text-foreground truncate text-sm font-medium">
            {title}
          </p>
          {badge}
        </div>

        <div className="text-muted-foreground flex items-center gap-3 text-xs">
          {ratingNode}
          {price != null && (
            <span>{formatCurrencyAmount(price, currency)}</span>
          )}
        </div>
      </div>

      <div className="shrink-0">{action}</div>
    </Card>
  );
};
